/**
 * React hooks and components for tracking element views
 *
 * @example Component usage (declarative)
 * <ElementViewTracker webElement={{ elementType: "card", name: "product-card", location: "product-grid" }}>
 *   <ProductCard />
 * </ElementViewTracker>
 *
 * @example Hook usage
 * const trackingRef = useTrackElementView({
 *   webElement: {
 *     elementType: "card",
 *     name: "product-card",
 *     location: "product-grid"
 *   }
 * });
 * return <div ref={trackingRef}>Product Card</div>;
 */

import React, { useDebugValue, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useCohesion } from '../../CohesionProvider';
import {
  useViewCorrelationId,
  ViewCorrelationIdProvider,
} from '../../ViewCorrelationIdProvider';
import ElementViewedEvent from './index';

const { createEventPayload } = ElementViewedEvent;

/**
 * @typedef {import('zod').infer<
 *   typeof import('./index').default.ReactComponentInputSchema
 * >} ElementViewedEventSchema
 */

/**
 * Tracks when an element is viewed by the user.
 * @param {ElementViewedEventSchema} eventData - Event data
 * @returns {React.RefCallback<HTMLDivElement>} The ref to attach to your element to observe its visibility
 */
export function useTrackElementView(eventData) {
  const [ref, inView, entry] = useInView({
    triggerOnce: true,
    threshold: 0.1, // 0.1 = 10% of the element must be in the viewport to consider the element "in-view"
  });

  const { trackEvent } = useCohesion();

  // Value to group related events together
  const viewCorrelationId = useViewCorrelationId();

  // Store latest event data in ref to avoid stale closures
  const latestEventDataRef = useRef(eventData);

  useEffect(() => {
    latestEventDataRef.current = {
      ...eventData,
      ...(viewCorrelationId && { viewCorrelationId }),
    };
  });

  useEffect(() => {
    if (!inView) return;

    trackEvent(createEventPayload(latestEventDataRef.current));
  }, [inView, trackEvent]);

  useDebugValue(
    {
      inView,
      isObserving: !!entry,
    },
    ({ inView, isObserving }) => {
      if (!isObserving) {
        return 'Not observing → Ref not attached (no children)';
      }
      return inView ? 'Visible' : 'Not visible';
    },
  );

  return ref;
}

/**
 * @typedef {{
 *   as?: React.ElementType, // Component to render as wrapper
 *   children: React.ReactNode,
 * }} ElementViewTrackerCommonProps
 */

/**
 * Component wrapper for tracking element views in Cohesion when `children` is in view
 * @param {ElementViewTrackerCommonProps & ElementViewedEventSchema} props
 */
function ElementViewTrackerBase({
  children,
  as: Element = 'div',
  ...trackingProps
}) {
  const ref = useTrackElementView(trackingProps);

  return (
    <Element
      data-testid="element-view-tracker"
      // Only attach tracking ref when children exist to avoid tracking empty elements
      ref={children ? ref : null}
    >
      {children}
    </Element>
  );
}

/**
 * Tracks when an element becomes visible in the viewport and sends Cohesion event.
 *
 * Features:
 * - Automatically tracks when element enters viewport
 * - Prevents duplicate events per element per page session
 * - Optional view correlation ID for grouping related view events
 * - Supports custom wrapper elements via 'as' prop
 *
 * @param {ElementViewTrackerCommonProps & ElementViewedEventSchema & {
 *   hasViewCorrelationId?: boolean // Generate unique correlation ID for this view context
 * }} props - Component props
 * @returns {React.ReactElement} Wrapper element that tracks visibility
 */
export function ElementViewTracker({
  children,
  hasViewCorrelationId = false,
  ...restProps
}) {
  // Wrap with ViewCorrelationIdProvider when correlation ID is needed
  // This ensures useTrackElementView hook can access the correlation context
  if (hasViewCorrelationId) {
    return (
      <ViewCorrelationIdProvider>
        <ElementViewTrackerBase {...restProps}>
          {children}
        </ElementViewTrackerBase>
      </ViewCorrelationIdProvider>
    );
  }

  return (
    <ElementViewTrackerBase {...restProps}>{children}</ElementViewTrackerBase>
  );
}

/**
 * Conditionally adds view tracking to an element when `trackingConfig` is provided.
 * @param {ElementViewTrackerCommonProps & {
 *   hasViewCorrelationId?: boolean // Generate unique correlation ID for this view context
 *   trackingConfig: ElementViewedEventSchema | null,
 * }} props
 */
export function OptionalElementViewTracker({
  trackingConfig,
  children,
  ...restProps
}) {
  // Do not track when config is not provided
  if (!trackingConfig) {
    return children;
  }

  return (
    <ElementViewTracker {...restProps} {...trackingConfig}>
      {children}
    </ElementViewTracker>
  );
}
